/**
 * Device Context - Central state management for all devices
 * 
 * AI Agent Note: This is the primary state container for the application.
 * All device CRUD operations go through this context.
 * Validation logic is delegated to deviceValidation.ts
 * Tree utilities are in utils/deviceTree.ts
 * 
 * Now integrated with RigContext for multi-rig support.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { Device, ConnectionValidation } from '../types';
import { validateDeviceConnections } from './deviceValidation';
import { findDeviceById, getConnectedDevices } from '../utils/deviceTree';
import { useRigs } from './RigContext';
import { trackEvent, AnalyticsEvents } from '../services/analytics';

interface DeviceContextType {
  devices: Device[];
  addDevice: (device: Device) => void;
  updateDevice: (device: Device) => void;
  deleteDevice: (deviceId: string) => void;
  importRig: (devices: Device[]) => void;
  exportRig: () => Device[];
  clearRig: () => void;
  validateConnections: () => ConnectionValidation;
  getDeviceById: (id: string) => Device | undefined;
  getConnectedDevices: (parentId: string) => Device[];
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const useDevices = (): DeviceContextType => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevices must be used within a DeviceProvider');
  }
  return context;
};

interface DeviceProviderProps {
  children: ReactNode;
}

export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  const { activeRig, updateRigDevices, activeRigId } = useRigs();
  const isSyncingFromRig = useRef(false);
  
  // Initialize from active rig
  const [devices, setDevices] = useState<Device[]>(() => {
    return activeRig?.devices || [];
  });

  // Sync with active rig when it changes
  useEffect(() => {
    isSyncingFromRig.current = true;
    setDevices(activeRig?.devices || []);
    // Reset flag after state update completes
    setTimeout(() => {
      isSyncingFromRig.current = false;
    }, 0);
  }, [activeRigId]);

  // Save to active rig whenever devices change (but not when syncing FROM rig)
  useEffect(() => {
    if (activeRigId && !isSyncingFromRig.current) {
      updateRigDevices(activeRigId, devices);
    }
  }, [devices, activeRigId, updateRigDevices]);

  const addDevice = useCallback((device: Device) => {
    setDevices(prev => [...prev, device]);
    
    // Track analytics
    trackEvent({ 
      eventType: AnalyticsEvents.DEVICE_ADDED,
      eventData: { deviceType: device.type }
    });
  }, []);

  const updateDevice = useCallback((device: Device) => {
    setDevices(prev => prev.map(d => d.id === device.id ? device : d));
    
    // Track analytics
    trackEvent({ 
      eventType: AnalyticsEvents.DEVICE_EDITED,
      eventData: { deviceType: device.type }
    });
  }, []);

  const deleteDevice = useCallback((deviceId: string) => {
    setDevices(prev => {
      // Also disconnect any devices connected to this one
      const updated = prev.map(d => 
        d.connectedTo === deviceId ? { ...d, connectedTo: null } : d
      );
      
      // Track analytics
      trackEvent({ eventType: AnalyticsEvents.DEVICE_DELETED });
      
      return updated.filter(d => d.id !== deviceId);
    });
  }, []);

  const importRig = useCallback((importedDevices: Device[]) => {
    setDevices(importedDevices);
  }, []);

  const exportRig = useCallback((): Device[] => {
    return devices;
  }, [devices]);

  const clearRig = useCallback(() => {
    if (window.confirm('⚠️ Clear entire rig? This will delete all devices and cannot be undone.')) {
      setDevices([]);
    }
  }, []);

  const getDeviceByIdCallback = useCallback((id: string): Device | undefined => {
    return findDeviceById(id, devices);
  }, [devices]);

  const getConnectedDevicesCallback = useCallback((parentId: string): Device[] => {
    return getConnectedDevices(parentId, devices);
  }, [devices]);

  const validateConnections = useCallback((): ConnectionValidation => {
    return validateDeviceConnections(devices);
  }, [devices]);

  const value: DeviceContextType = {
    devices,
    addDevice,
    updateDevice,
    deleteDevice,
    importRig,
    exportRig,
    clearRig,
    validateConnections,
    getDeviceById: getDeviceByIdCallback,
    getConnectedDevices: getConnectedDevicesCallback
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};
